import * as v from "valibot";
import {
  AnswerJsonSchema,
  IceCandidateJsonSchema,
  MediaStateJsonSchema,
  OfferJsonSchema,
} from "../../shared/schemas";
import type { Telemetry } from "../../shared/api";
import { isLiveCall, ringMs } from "../../shared/calls";
import type {
  CallMedia,
  CallView,
  IceSignal,
  Person,
} from "../../shared/types";
import { ApiError } from "../data/api";
import type { ClientConnectionEvent } from "../data/realtime";
import { t } from "../i18n";
import type { Sound } from "../sounds/sounds";
import { CallRecovery } from "./call-recovery";
import { CallSounds } from "./call-sounds";
import {
  isRinging,
  isTalking,
  type Call,
  type CallStage,
  type CallStatus,
  type EndedCall,
} from "./call-state";
import { mediaError } from "./devices";

// The server ends an unanswered ring and announces it. This later timer covers
// an announcement that never arrives.
const ringGiveUpMs = ringMs + 2000;

type Transport = Extract<Telemetry, { kind: "call" }>["transport"];
type Notify = (message: string, tone?: "success" | "missed") => void;

type CallSignaling = {
  connected(): boolean;
  subscribe(listener: (event: ClientConnectionEvent) => void): () => void;
  readCall(
    id: string,
    timeoutMs?: number,
  ): Promise<{ call: CallView; signals: IceSignal[] }>;
  iceServers(personId: string, timeoutMs?: number): Promise<RTCIceServer[]>;
  placeCall(
    personId: string,
    media: CallMedia,
    offer: RTCSessionDescription | null,
  ): Promise<{ id: string }>;
  answerCall(id: string, answer: RTCSessionDescription | null): Promise<void>;
  endCall(id: string): Promise<void>;
  endCallOnLeave(id: string): void;
  requestRestart(id: string, revision: number): Promise<void>;
  sendRestartDescription(
    id: string,
    revision: number,
    description: RTCSessionDescriptionInit,
  ): Promise<void>;
  sendCandidate(
    id: string,
    candidate: RTCIceCandidateInit,
    revision?: number,
  ): Promise<void>;
  announceAlive(id: string): void;
};

type CallDevices = {
  captureCall(camera: boolean): Promise<MediaStream>;
  // At call quality; without a choice, the camera picked in the microphone check.
  captureCamera(choice?: MediaTrackConstraints): Promise<MediaStream>;
  // Must reach the picker synchronously to keep the click's user activation.
  captureScreen(): Promise<MediaStream>;
  canShareScreen(): boolean;
  listCameras(): Promise<MediaDeviceInfo[]>;
  watchDevices(listener: () => void): () => void;
  stream(tracks: MediaStreamTrack[]): MediaStream;
  watchLevel(stream: MediaStream, onLevel: (level: number) => void): () => void;
  speaker(heard: MediaStream, muted: boolean): HTMLAudioElement;
};

type CallPage = {
  hidden(): boolean;
  online(): boolean;
  listen(on: {
    network(): void;
    visibility(): void;
    leave(): void;
  }): () => void;
  confirmLeaving(): () => void;
};

export type CallPorts = {
  createPeer(config: RTCConfiguration): RTCPeerConnection;
  signaling: CallSignaling;
  devices: CallDevices;
  page: CallPage;
  sounds: {
    play(sound: Sound): boolean;
    stop(channel?: "message" | "preview"): void;
    listen(): () => void;
    onUnlock(listener: () => void): () => void;
  };
  telemetry: {
    report(event: Telemetry): void;
    selectedTransport(peer: RTCPeerConnection): Promise<Transport>;
  };
};

export type CallContext = {
  notify: Notify;
  userId: string | undefined;
  systemAlerts: boolean;
};

export type CallSnapshot = { call: Call | null; ended: EndedCall | null };

// Everything private that dies with the call. Replaced whole, so nothing is missed.
type Wires = {
  outgoing: boolean;
  peer: RTCPeerConnection | null;
  recovery: CallRecovery | null;
  microphone: MediaStream | null;
  output: HTMLAudioElement | null;
  stopMeter: (() => void) | null;
  audioSender: RTCRtpSender | null;
  videoSender: RTCRtpSender | null;
  stateChannel: RTCDataChannel | null;
  screen: MediaStream | null;
  restoreCamera: boolean;
  timer: ReturnType<typeof setTimeout> | null;
  // Reported when the call is torn down; no names, ids or media leave.
  outcome: {
    media: CallMedia;
    connectedAt: number | null;
    transport?: Transport;
    reconnects: number;
  } | null;
  localCandidates: RTCIceCandidateInit[];
  pending: Map<number, IceSignal>;
  seen: Set<number>;
  queue: Promise<void>;
  cameraChanging: boolean;
  microphoneChanging: boolean;
};

const freshWires = (): Wires => ({
  outgoing: false,
  peer: null,
  recovery: null,
  microphone: null,
  output: null,
  stopMeter: null,
  audioSender: null,
  videoSender: null,
  stateChannel: null,
  screen: null,
  restoreCamera: false,
  timer: null,
  outcome: null,
  localCandidates: [],
  pending: new Map(),
  seen: new Set(),
  queue: Promise.resolve(),
  cameraChanging: false,
  microphoneChanging: false,
});

const newCall = (
  stage: CallStage & Pick<Call, "person" | "media" | "supportsScreenSharing">,
): Call => ({
  ...stage,
  seconds: 0,
  muted: false,
  microphoneStopped: false,
  microphoneBusy: false,
  level: 0,
  deafened: false,
  playbackBlocked: false,
  localStream: null,
  remoteStream: null,
  remoteAudio: null,
  cameraOff: true,
  cameraBusy: false,
  sharingScreen: false,
  remoteCameraOff: true,
  remoteMuted: false,
  remoteSharingScreen: false,
  canSendVideo: false,
  canFlipCamera: false,
  canShareScreen: false,
});

const sameCall = (a: Call | null, b: Call | null) =>
  a === b ||
  (!!a &&
    !!b &&
    (Object.keys(b) as (keyof Call)[]).every((key) => a[key] === b[key]));

// What the person hears when the server says a call has stopped. A ring that
// stops by itself was missed; declining on another device needs no notice here.
// An answered call says it ended on its own card.
function endingNotice(
  status: CallStatus,
  server: CallView["status"],
  answered: boolean,
  name: string,
): { message: string; tone?: "success" | "missed" } | null {
  if (status === "incoming")
    return server === "declined"
      ? null
      : { message: t("مكالمة فائتة من {name}", { name }), tone: "missed" };
  if (server === "ended" || server === "cancelled")
    return answered ? null : { message: "المكالمة انتهت", tone: "success" };
  return {
    message:
      server === "missed"
        ? "لا يوجد رد. حاول لاحقًا."
        : server === "declined"
          ? "الطرف الآخر مشغول حاليًا"
          : "الاتصال انقطع وانتهت المكالمة.",
  };
}

const lostAccess = (error: unknown) =>
  error instanceof ApiError && [401, 403, 404].includes(error.status);

// One call at a time, from the first sight of it until its media is released.
// A generation token guards every continuation: start, accept and dismiss bump
// it, and anything resuming across an await with an older token gives up.
export class CallSession {
  private snapshot: CallSnapshot = { call: null, ended: null };
  private listeners = new Set<() => void>();
  private ports: CallPorts;
  private context: CallContext;
  private callSounds: CallSounds;
  private generation = 0;
  private busy = false;
  private wires = freshWires();
  private cameraCount = 0;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private ticker: ReturnType<typeof setInterval> | null = null;
  private stopConfirmingLeave: (() => void) | null = null;
  private stopWatchingDevices: (() => void) | null = null;

  constructor(ports: CallPorts, context: CallContext) {
    this.ports = ports;
    this.context = context;
    this.callSounds = new CallSounds({
      play: (sound) => {
        // Hidden subscribed devices already have the system's call alert.
        if (
          sound === "incoming" &&
          this.ports.page.hidden() &&
          this.context.systemAlerts
        )
          return false;
        return this.ports.sounds.play(sound);
      },
      stop: () => this.ports.sounds.stop(),
    });
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = () => this.snapshot;

  configure = (context: CallContext) => {
    const changed = context.userId !== this.context.userId;
    this.context = context;
    if (changed) this.silence();
  };

  attach = () => {
    const { signaling, sounds, page } = this.ports;
    const stops = [
      signaling.subscribe(this.receiveEvent),
      sounds.listen(),
      sounds.onUnlock(this.callSounds.ring),
      page.listen({
        network: () => {
          this.wires.recovery?.recover();
          this.wires.recovery?.resume();
        },
        visibility: () => {
          if (!isRinging(this.call)) return;
          sounds.stop();
          this.callSounds.ring();
        },
        leave: () => {
          this.silence();
          const id = this.call?.id;
          if (id) signaling.endCallOnLeave(id);
          this.dismiss();
        },
      }),
    ];
    return () => {
      for (const stop of stops) stop();
      this.silence();
      this.dismiss();
    };
  };

  private get call() {
    return this.snapshot.call;
  }

  private silence() {
    this.callSounds.reset();
    this.ports.sounds.stop("message");
    this.ports.sounds.stop("preview");
  }

  private publish(call: Call | null, ended: EndedCall | null) {
    const previous = this.snapshot,
      next = call && this.derive(call);
    if (sameCall(previous.call, next) && previous.ended === ended) return;
    this.snapshot = { call: next, ended };
    this.follow(previous.call, next);
    for (const listener of this.listeners) listener();
  }

  private derive(call: Call): Call {
    const connected = call.status === "connected",
      { peer, videoSender } = this.wires;
    return {
      ...call,
      canShareScreen: this.ports.devices.canShareScreen(),
      canFlipCamera:
        connected &&
        this.cameraCount > 1 &&
        !call.cameraOff &&
        !call.sharingScreen,
      canSendVideo:
        connected &&
        !!videoSender &&
        peer
          ?.getTransceivers()
          .some(
            (item) =>
              item.sender === videoSender &&
              (item.currentDirection === "sendrecv" ||
                item.currentDirection === "sendonly"),
          ) === true,
    };
  }

  // What keeps running only while the call is in a given state.
  private follow(before: Call | null, after: Call | null) {
    if (before?.id !== after?.id) {
      if (this.heartbeat) clearInterval(this.heartbeat);
      this.heartbeat = null;
      const id = after?.id;
      if (id) {
        const beat = () => this.ports.signaling.announceAlive(id);
        beat();
        this.heartbeat = setInterval(beat, 20000);
      }
    }
    const connected = after?.status === "connected";
    if ((before?.status === "connected") !== connected) {
      if (this.ticker) clearInterval(this.ticker);
      this.ticker = connected
        ? setInterval(() => {
            if (this.call) this.patch({ seconds: this.call.seconds + 1 });
          }, 1000)
        : null;
    }
    // Leaving the page hangs up, so closing the wrong tab asks first. The listener
    // exists only during a call because it can keep a page out of the back cache.
    if (isTalking(before) !== isTalking(after)) {
      this.stopConfirmingLeave?.();
      this.stopConfirmingLeave = isTalking(after)
        ? this.ports.page.confirmLeaving()
        : null;
    }
    if (before?.localStream !== after?.localStream) {
      this.stopWatchingDevices?.();
      this.stopWatchingDevices = null;
      if (after?.localStream) {
        const read = () =>
          void this.ports.devices
            .listCameras()
            .then((cameras) => {
              this.cameraCount = cameras.length;
              this.publish(this.call, this.snapshot.ended);
            })
            .catch(() => {});
        read();
        this.stopWatchingDevices = this.ports.devices.watchDevices(read);
      }
    }
  }

  private move(next: Call | null) {
    if (next) {
      this.ports.sounds.stop("message");
      this.ports.sounds.stop("preview");
    }
    this.callSounds.update(next);
    this.publish(next, next ? null : this.snapshot.ended);
  }

  private patch(fields: Partial<Omit<Call, keyof CallStage>>) {
    if (this.call)
      this.publish({ ...this.call, ...fields }, this.snapshot.ended);
  }

  private teardown() {
    const wires = this.wires;
    this.wires = freshWires();
    if (wires.outcome) {
      const { media, connectedAt, transport, reconnects } = wires.outcome;
      this.ports.telemetry.report({
        kind: "call",
        media,
        connected: connectedAt !== null,
        seconds: connectedAt
          ? Math.min(86400, Math.round((Date.now() - connectedAt) / 1000))
          : 0,
        transport,
        reconnects,
      });
    }
    if (wires.timer) clearTimeout(wires.timer);
    wires.recovery?.dispose();
    wires.stateChannel?.close();
    wires.screen?.getTracks().forEach((track) => track.stop());
    wires.peer?.close();
    wires.microphone?.getTracks().forEach((track) => track.stop());
    if (wires.output) {
      wires.output.onpause = null;
      wires.output.onplaying = null;
      wires.output.pause();
      wires.output.srcObject = null;
    }
    wires.stopMeter?.();
  }

  private dismiss() {
    this.generation++;
    this.move(null);
    this.teardown();
  }

  private holdEnding() {
    const call = this.call,
      connectedAt = this.wires.outcome?.connectedAt;
    if (call && connectedAt)
      this.publish(call, {
        person: call.person,
        media: call.media,
        seconds: Math.floor((Date.now() - connectedAt) / 1000),
      });
  }

  dismissEnded = () => this.publish(this.call, null);

  end = async (showEnding = true) => {
    const id = this.call?.id;
    if (showEnding) this.holdEnding();
    this.dismiss();
    if (id)
      try {
        await this.ports.signaling.endCall(id);
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 401))
          this.context.notify("انتهت المكالمة على جهازك.");
      }
  };

  private async flushRemote() {
    const { peer, pending, seen } = this.wires;
    const token = this.generation;
    if (!peer?.remoteDescription) return;
    for (const signal of pending.values()) {
      const revision = this.wires.recovery?.remoteRevision ?? 0;
      if (signal.revision > revision) continue;
      if (signal.revision < revision) {
        pending.delete(signal.id);
        continue;
      }
      if (!seen.has(signal.id)) {
        await peer.addIceCandidate(
          v.parse(IceCandidateJsonSchema, signal.payload),
        );
        if (token !== this.generation) return;
        seen.add(signal.id);
      }
      pending.delete(signal.id);
    }
  }

  // ICE and SDP apply one at a time, in the order they arrived.
  private enqueue(
    token: number,
    step: () => Promise<void> | void,
    failed: () => void,
  ) {
    this.wires.queue = this.wires.queue
      .catch(() => {})
      .then(step)
      .catch(() => {
        if (token === this.generation) failed();
      });
  }

  private applyCall(value: CallView, signals: IceSignal[] = []) {
    const active = this.call;
    if (!active || active.id !== value.id) return;
    for (const signal of signals) this.wires.pending.set(signal.id, signal);
    if (!isLiveCall(value.status)) {
      const notice = endingNotice(
        active.status,
        value.status,
        !!this.wires.outcome?.connectedAt,
        active.person.name,
      );
      this.holdEnding();
      this.dismiss();
      if (notice) this.context.notify(notice.message, notice.tone);
      return;
    }
    if (value.status === "active" && active.status === "incoming") {
      this.callSounds.reset();
      this.dismiss();
      this.context.notify("تم الرد على المكالمة من جهاز آخر.");
      return;
    }
    if (value.status === "active" && active.status === "calling")
      this.move({ ...active, status: "connecting" });
    const token = this.generation;
    this.enqueue(
      token,
      async () => {
        if (token !== this.generation || this.call?.id !== value.id) return;
        const { peer, outgoing } = this.wires;
        if (peer && value.answer && !peer.remoteDescription && outgoing)
          await peer.setRemoteDescription(
            v.parse(AnswerJsonSchema, value.answer),
          );
        try {
          await this.wires.recovery?.apply(value.recovery);
        } catch (error) {
          if (token !== this.generation) return;
          if (lostAccess(error)) {
            this.dismiss();
            return;
          }
          this.wires.recovery?.recover();
        }
        if (token !== this.generation) return;
        await this.flushRemote();
      },
      () => {
        this.context.notify("تعذر توصيل المكالمة. اتصل مجددًا.");
        void this.end();
      },
    );
  }

  receive = (value: CallView | null, meId: string) => {
    if (
      value &&
      value.callee === meId &&
      value.status === "ringing" &&
      !this.call
    )
      this.move(
        newCall({
          id: value.id,
          status: "incoming",
          person: value.person,
          media: value.media ?? "audio",
          supportsScreenSharing: value.supportsScreenSharing ?? false,
        }),
      );
  };

  private async synchronize(id: string) {
    try {
      const data = await this.ports.signaling.readCall(id, 10000);
      this.applyCall(data.call, data.signals);
    } catch (e) {
      if (this.call?.id === id && lostAccess(e)) this.dismiss();
    }
  }

  refresh = () => {
    const id = this.call?.id;
    return id ? this.synchronize(id) : Promise.resolve();
  };

  private receiveEvent = (event: ClientConnectionEvent) => {
    if (event.type === "call") {
      if (this.context.userId) this.receive(event.call, this.context.userId);
      this.applyCall(event.call);
    }
    if (event.type === "signal" && this.call?.id === event.callId) {
      const token = this.generation;
      this.wires.pending.set(event.signal.id, event.signal);
      this.enqueue(
        token,
        () => (token === this.generation ? this.flushRemote() : undefined),
        () => this.wires.recovery?.recover(),
      );
    }
    const id = this.call?.id;
    if (event.type === "ready" && id) {
      void this.synchronize(id);
      this.ports.signaling.announceAlive(id);
      this.wires.recovery?.resume();
    }
    if (event.type === "session-ended") {
      this.silence();
      this.dismiss();
    }
  };

  private async capture(video = false) {
    const token = this.generation,
      media = await this.ports.devices.captureCall(video);
    if (token !== this.generation) {
      media.getTracks().forEach((track) => track.stop());
      throw new Error("تم إلغاء المكالمة");
    }
    this.wires.microphone = media;
    for (const track of media.getAudioTracks())
      track.enabled = !this.call?.muted;
    if (video)
      this.patch({
        localStream: this.ports.devices.stream(media.getVideoTracks()),
      });
    this.startMeter(media);
    return media;
  }

  private startMeter(media: MediaStream) {
    this.wires.stopMeter?.();
    this.wires.stopMeter = this.ports.devices.watchLevel(media, (level) =>
      this.patch({ level }),
    );
  }

  private sendMediaState() {
    const { stateChannel, videoSender, screen } = this.wires;
    if (stateChannel?.readyState === "open")
      stateChannel.send(
        JSON.stringify({
          cameraOn: videoSender?.track?.readyState === "live",
          sharingScreen: !!screen,
          muted: !!this.call?.muted,
        }),
      );
  }

  private attachStateChannel(channel: RTCDataChannel, token: number) {
    if (token !== this.generation) {
      channel.close();
      return;
    }
    if (channel.label !== "media-state" || this.wires.stateChannel) {
      channel.close();
      return;
    }
    this.wires.stateChannel = channel;
    channel.onopen = () => {
      if (token === this.generation) this.sendMediaState();
    };
    channel.onmessage = (event) => {
      if (token !== this.generation) return;
      const result = v.safeParse(MediaStateJsonSchema, event.data);
      if (result.success)
        this.patch({
          remoteCameraOff: !result.output.cameraOn,
          remoteSharingScreen: result.output.sharingScreen,
          remoteMuted: result.output.muted,
        });
    };
  }

  private watchMicrophone(
    track: MediaStreamTrack,
    sender: RTCRtpSender,
    token: number,
  ) {
    const stopped = () => {
      if (token !== this.generation || sender.track !== track) return;
      this.wires.microphone?.removeTrack(track);
      this.wires.stopMeter?.();
      this.wires.stopMeter = null;
      this.patch({ level: 0, microphoneStopped: true });
    };
    track.onended = stopped;
    if (track.readyState === "ended") stopped();
  }

  restartMicrophone = async () => {
    const wires = this.wires,
      sender = wires.audioSender,
      media = wires.microphone,
      token = this.generation;
    if (!sender || !media || wires.microphoneChanging) return;
    wires.microphoneChanging = true;
    this.patch({ microphoneBusy: true });
    let acquired: MediaStream | null = null;
    try {
      acquired = await this.ports.devices.captureCall(false);
      if (token !== this.generation) return;
      const track = acquired.getAudioTracks()[0];
      if (!track || track.readyState !== "live")
        throw new Error("لا يتوفر ميكروفون.");
      track.enabled = !this.call?.muted;
      await sender.replaceTrack(track);
      if (token !== this.generation) return;
      media.addTrack(track);
      acquired = null;
      this.patch({ microphoneStopped: false });
      this.watchMicrophone(track, sender, token);
      if (track.readyState === "live")
        this.startMeter(this.ports.devices.stream([track]));
    } catch (error) {
      if (token === this.generation)
        this.context.notify(mediaError(error, false));
    } finally {
      acquired?.getTracks().forEach((track) => track.stop());
      if (token === this.generation) {
        wires.microphoneChanging = false;
        this.patch({ microphoneBusy: false });
      }
    }
  };

  private watchCamera(
    track: MediaStreamTrack,
    sender: RTCRtpSender,
    token: number,
  ) {
    track.onended = () => {
      if (token !== this.generation || sender.track !== track) return;
      this.wires.microphone?.removeTrack(track);
      this.patch({ localStream: null, cameraOff: true });
      void sender
        .replaceTrack(null)
        .then(() => {
          if (token === this.generation) this.sendMediaState();
        })
        .catch(() => {});
    };
  }

  private async playAudio(output: HTMLAudioElement) {
    const token = this.generation;
    try {
      await output.play();
      if (token === this.generation && this.wires.output === output)
        this.patch({ playbackBlocked: false });
    } catch {
      if (
        token === this.generation &&
        this.wires.output === output &&
        !this.call?.deafened
      )
        this.patch({ playbackBlocked: true });
    }
  }

  private async makePeer(
    person: Person,
    mediaType: CallMedia,
    outgoing: boolean,
    supportsScreenSharing: boolean,
    camera = mediaType === "video",
  ) {
    const token = this.generation,
      iceServers = await this.ports.signaling.iceServers(person.id);
    if (token !== this.generation) throw new Error("تم إلغاء المكالمة");
    const media = await this.capture(camera);
    this.patch({ cameraOff: !camera });
    const peer = this.ports.createPeer({
      iceServers,
      // Audio, reserved video and control data share one ICE transport from
      // the first offer, avoiding three candidate bursts before an answer.
      bundlePolicy: "max-bundle",
    });
    const wires = this.wires;
    wires.peer = peer;
    wires.outgoing = outgoing;
    wires.outcome = { media: mediaType, connectedAt: null, reconnects: 0 };
    for (const track of media.getTracks()) {
      const sender = peer.addTrack(track, media);
      if (track.kind === "audio") {
        wires.audioSender = sender;
        this.watchMicrophone(track, sender, token);
      }
      if (track.kind === "video") {
        wires.videoSender = sender;
        this.watchCamera(track, sender, token);
      }
    }
    if (mediaType === "audio" && supportsScreenSharing && outgoing)
      wires.videoSender = peer.addTransceiver("video", {
        direction: "sendrecv",
      }).sender;
    if (mediaType === "video" || supportsScreenSharing) {
      if (outgoing)
        this.attachStateChannel(peer.createDataChannel("media-state"), token);
      else
        peer.ondatachannel = (event) =>
          this.attachStateChannel(event.channel, token);
    }
    peer.ontrack = (event) => {
      if (token !== this.generation) return;
      if (event.track.kind === "video") {
        this.patch({
          remoteStream: this.ports.devices.stream([event.track]),
        });
        return;
      }
      // Play audio once, independently of the muted video elements.
      const heard = this.ports.devices.stream([event.track]);
      const output = this.ports.devices.speaker(heard, !!this.call?.deafened);
      this.patch({ remoteAudio: heard });
      this.wires.output = output;
      output.onplaying = () => {
        if (token === this.generation && this.wires.output === output)
          this.patch({ playbackBlocked: false });
      };
      output.onpause = () => {
        if (
          token === this.generation &&
          this.wires.output === output &&
          !this.call?.deafened
        )
          this.patch({ playbackBlocked: true });
      };
      void this.playAudio(output);
    };
    peer.onicecandidate = (event) => {
      if (!event.candidate || token !== this.generation) return;
      const fragment = event.candidate.usernameFragment;
      if (
        fragment &&
        !peer.localDescription?.sdp.includes(`a=ice-ufrag:${fragment}\r\n`)
      )
        return;
      const id = this.call?.id;
      if (id)
        void this.ports.signaling
          .sendCandidate(
            id,
            event.candidate.toJSON(),
            this.wires.recovery?.localRevision,
          )
          .catch(
            () => token === this.generation && this.wires.recovery?.recover(),
          );
      else this.wires.localCandidates.push(event.candidate.toJSON());
    };
    wires.recovery = new CallRecovery(peer, {
      outgoing,
      available: () =>
        token === this.generation &&
        this.ports.signaling.connected() &&
        this.ports.page.online(),
      restart: async (revision) => {
        const id = this.call?.id;
        if (!id || token !== this.generation) return;
        try {
          await this.ports.signaling.requestRestart(id, revision);
        } catch (error) {
          if (token === this.generation && lostAccess(error)) this.dismiss();
          throw error;
        }
        if (token === this.generation) await this.synchronize(id);
      },
      describe: async (revision, description) => {
        const id = this.call?.id;
        if (!id || token !== this.generation) return;
        await this.ports.signaling.sendRestartDescription(
          id,
          revision,
          description,
        );
      },
      refreshIce: () => this.ports.signaling.iceServers(person.id, 10000),
      reconnecting: () => {
        const call = this.call;
        if (token !== this.generation || !call?.id) return;
        if (this.wires.outcome) this.wires.outcome.reconnects++;
        this.clearTimer();
        this.move({ ...call, status: "reconnecting" });
      },
      connected: () => {
        const call = this.call;
        if (token !== this.generation || !call?.id) return;
        this.clearTimer();
        this.move({ ...call, status: "connected" });
        this.sendMediaState();
        const { outcome } = this.wires;
        if (outcome && outcome.connectedAt === null) {
          outcome.connectedAt = Date.now();
          void this.ports.telemetry
            .selectedTransport(peer)
            .then((transport) => {
              if (token === this.generation) outcome.transport = transport;
            })
            .catch(() => {});
        }
      },
      failed: () => {
        if (token !== this.generation) return;
        this.context.notify("تعذر استعادة الاتصال. اتصل مجددًا.");
        void this.end();
      },
    });
    return peer;
  }

  private clearTimer() {
    if (this.wires.timer) clearTimeout(this.wires.timer);
    this.wires.timer = null;
  }

  private async flushLocal(id: string) {
    const candidates = this.wires.localCandidates;
    this.wires.localCandidates = [];
    for (const candidate of candidates)
      await this.ports.signaling.sendCandidate(id, candidate);
  }

  start = async (person: Person, media: CallMedia = "audio") => {
    if (this.call || this.busy) return;
    if (!this.ports.signaling.connected()) {
      this.context.notify("جارٍ تجهيز الاتصال. حاول بعد لحظة.");
      return;
    }
    this.busy = true;
    const token = ++this.generation;
    try {
      this.move(
        newCall({
          id: null,
          status: "starting",
          person,
          media,
          supportsScreenSharing: true,
        }),
      );
      const peer = await this.makePeer(person, media, true, true);
      if (token !== this.generation) return;
      await peer.setLocalDescription(await peer.createOffer());
      if (token !== this.generation) return;
      const { id } = await this.ports.signaling.placeCall(
        person.id,
        media,
        peer.localDescription,
      );
      if (token !== this.generation) {
        await this.ports.signaling.endCall(id);
        return;
      }
      this.move({ ...this.call!, id, status: "calling" });
      await this.flushLocal(id);
      await this.synchronize(id);
      if (token !== this.generation) return;
      if (peer.connectionState !== "connected")
        this.wires.timer = setTimeout(() => {
          this.context.notify("لا يوجد رد.");
          void this.end();
        }, ringGiveUpMs);
    } catch (e) {
      if (token === this.generation) {
        this.context.notify(mediaError(e, media === "video"));
        await this.end();
      }
    } finally {
      this.busy = false;
    }
  };

  // A video call can be answered with audio alone and the camera turned on later.
  accept = async (answer?: CallMedia) => {
    const value = this.call;
    if (!value?.id || this.busy) return;
    this.busy = true;
    const token = ++this.generation,
      camera = value.media === "video" && answer !== "audio";
    try {
      this.move({ ...value, status: "connecting" });
      const data = await this.ports.signaling.readCall(value.id);
      if (token !== this.generation) return;
      if (data.call.status !== "ringing")
        throw new ApiError("انتهت المكالمة أو تم الرد عليها.", 409);
      const peer = await this.makePeer(
        value.person,
        value.media,
        false,
        value.supportsScreenSharing,
        camera,
      );
      if (token !== this.generation) return;
      await peer.setRemoteDescription(
        v.parse(OfferJsonSchema, data.call.offer),
      );
      if (!camera && (value.media === "video" || value.supportsScreenSharing)) {
        // Answer using the offered transceiver; pre-creating one on the callee
        // leaves an unassociated sender that cannot transmit without negotiation.
        const video = peer
          .getTransceivers()
          .find((item) => item.receiver.track.kind === "video");
        if (video) {
          video.direction = "sendrecv";
          this.wires.videoSender = video.sender;
        }
      }
      for (const signal of data.signals)
        this.wires.pending.set(signal.id, signal);
      await this.flushRemote();
      await peer.setLocalDescription(await peer.createAnswer());
      if (token !== this.generation) return;
      await this.ports.signaling.answerCall(value.id, peer.localDescription);
      await this.flushLocal(value.id);
      if (token !== this.generation) return;
      if (peer.connectionState !== "connected")
        this.wires.timer = setTimeout(() => {
          this.context.notify("تعذر توصيل المكالمة. حاول مجددًا.");
          void this.end();
        }, 30000);
    } catch (e) {
      if (token === this.generation) {
        this.context.notify(mediaError(e, camera));
        if (e instanceof ApiError && e.status === 409) this.dismiss();
        else await this.end();
      }
    } finally {
      this.busy = false;
    }
  };

  toggleMute = () => {
    const call = this.call;
    if (!call) return;
    const value = !call.muted;
    for (const track of this.wires.microphone?.getAudioTracks() || [])
      track.enabled = !value;
    this.patch({ muted: value });
    this.sendMediaState();
  };

  togglePlayback = () => {
    const call = this.call;
    if (!call) return;
    const output = this.wires.output;
    // A paused or blocked player needs a user-initiated play(), not a mute.
    const value = !(call.deafened || call.playbackBlocked || output?.paused);
    this.patch({ deafened: value });
    if (output) {
      output.muted = value;
      if (!value) void this.playAudio(output);
    }
    if (value) this.patch({ playbackBlocked: false });
  };

  private settleCamera(wires: Wires, token: number) {
    if (token !== this.generation) return false;
    wires.cameraChanging = false;
    this.patch({ cameraBusy: false });
    return true;
  }

  toggleCamera = async () => {
    const wires = this.wires,
      sender = wires.videoSender,
      media = wires.microphone,
      token = this.generation;
    if (
      !sender ||
      !media ||
      this.call?.status !== "connected" ||
      wires.cameraChanging ||
      wires.screen
    )
      return;
    wires.cameraChanging = true;
    this.patch({ cameraBusy: true });
    let acquired: MediaStream | null = null;
    try {
      if (sender.track) {
        const track = sender.track;
        await sender.replaceTrack(null);
        track.stop();
        media.removeTrack(track);
        if (token !== this.generation) return;
        this.patch({ localStream: null, cameraOff: true });
      } else {
        acquired = await this.ports.devices.captureCamera();
        if (token !== this.generation) return;
        const track = acquired.getVideoTracks()[0]!;
        await sender.replaceTrack(track);
        if (token !== this.generation) return;
        media.addTrack(track);
        this.watchCamera(track, sender, token);
        this.patch({
          localStream: this.ports.devices.stream([track]),
          cameraOff: false,
        });
        acquired = null;
      }
      this.sendMediaState();
    } catch (error) {
      if (token === this.generation)
        this.context.notify(mediaError(error, true));
    } finally {
      acquired?.getTracks().forEach((track) => track.stop());
      this.settleCamera(wires, token);
    }
  };

  // Phones can't hold two cameras at once, so the old one stops first. They name
  // their cameras by the way they face, with several on the back; anything else
  // moves on to the next one in the list.
  flipCamera = async () => {
    const wires = this.wires,
      sender = wires.videoSender,
      media = wires.microphone,
      track = sender?.track,
      token = this.generation;
    if (!sender || !media || !track || wires.cameraChanging || wires.screen)
      return;
    wires.cameraChanging = true;
    this.patch({ cameraBusy: true });
    const { deviceId, facingMode } = track.getSettings();
    let acquired: MediaStream | null = null;
    try {
      const cameras = await this.ports.devices.listCameras();
      const next =
        cameras[
          (cameras.findIndex((camera) => camera.deviceId === deviceId) + 1) %
            cameras.length
        ]?.deviceId;
      track.stop();
      media.removeTrack(track);
      const choices: MediaTrackConstraints[] = [
        ...(facingMode
          ? [
              {
                facingMode: {
                  exact: facingMode === "user" ? "environment" : "user",
                },
              },
            ]
          : []),
        { deviceId: { exact: next } },
      ];
      for (const [index, choice] of choices.entries())
        try {
          acquired = await this.ports.devices.captureCamera(choice);
          break;
        } catch (error) {
          if (index === choices.length - 1) throw error;
        }
      if (token !== this.generation || !acquired) return;
      const flipped = acquired.getVideoTracks()[0]!;
      await sender.replaceTrack(flipped);
      if (token !== this.generation) return;
      media.addTrack(flipped);
      this.watchCamera(flipped, sender, token);
      this.patch({ localStream: this.ports.devices.stream([flipped]) });
      acquired = null;
    } catch (error) {
      if (token === this.generation) {
        // The old camera has already stopped, so this is the camera off.
        await sender.replaceTrack(null).catch(() => {});
        if (token !== this.generation) return;
        this.patch({ localStream: null, cameraOff: true });
        this.sendMediaState();
        this.context.notify(mediaError(error, true));
      }
    } finally {
      acquired?.getTracks().forEach((item) => item.stop());
      this.settleCamera(wires, token);
    }
  };

  stopScreenShare = async () => {
    const wires = this.wires,
      display = wires.screen,
      sender = wires.videoSender,
      media = wires.microphone,
      token = this.generation;
    if (!display || !sender || !media || wires.cameraChanging) return;
    wires.cameraChanging = true;
    const resumeCamera = wires.restoreCamera;
    wires.restoreCamera = false;
    wires.screen = null;
    display.getTracks().forEach((track) => track.stop());
    this.patch({
      cameraBusy: true,
      sharingScreen: false,
      localStream: null,
      cameraOff: true,
    });
    let acquired: MediaStream | null = null;
    try {
      await sender.replaceTrack(null);
      if (token !== this.generation) return;
      this.sendMediaState();
      if (resumeCamera) {
        acquired = await this.ports.devices.captureCamera();
        if (token !== this.generation) return;
        const track = acquired.getVideoTracks()[0]!;
        await sender.replaceTrack(track);
        if (token !== this.generation) return;
        media.addTrack(track);
        this.watchCamera(track, sender, token);
        this.patch({
          localStream: this.ports.devices.stream([track]),
          cameraOff: false,
        });
        acquired = null;
        this.sendMediaState();
      }
    } catch (error) {
      if (token === this.generation) {
        this.sendMediaState();
        this.context.notify(mediaError(error, true));
      }
    } finally {
      acquired?.getTracks().forEach((track) => track.stop());
      this.settleCamera(wires, token);
    }
  };

  startScreenShare = async () => {
    const wires = this.wires,
      sender = wires.videoSender,
      token = this.generation;
    if (
      !sender ||
      this.call?.status !== "connected" ||
      wires.cameraChanging ||
      wires.screen
    )
      return;
    if (!this.ports.devices.canShareScreen()) {
      this.context.notify("هذا المتصفح لا يدعم مشاركة الشاشة.");
      return;
    }
    const direction = wires.peer
      ?.getTransceivers()
      .find((item) => item.sender === sender)?.currentDirection;
    if (direction !== "sendrecv" && direction !== "sendonly") {
      this.context.notify(
        "مشاركة الشاشة غير متاحة في هذه المكالمة. اتصل مجددًا.",
      );
      return;
    }
    wires.cameraChanging = true;
    this.patch({ cameraBusy: true });
    let acquired: MediaStream | null = null;
    try {
      // Keep the picker directly in the click handler's user activation.
      acquired = await this.ports.devices.captureScreen();
      if (token !== this.generation) return;
      const track = acquired.getVideoTracks()[0];
      if (!track || track.readyState !== "live") return;
      track.contentHint = "detail";
      const camera = sender.track;
      await sender.replaceTrack(track);
      if (token !== this.generation) return;
      wires.restoreCamera = !!camera && camera.readyState === "live";
      camera?.stop();
      if (camera) wires.microphone?.removeTrack(camera);
      wires.screen = acquired;
      this.patch({
        localStream: this.ports.devices.stream([track]),
        sharingScreen: true,
        cameraOff: true,
      });
      track.onended = () => {
        if (token === this.generation) void this.stopScreenShare();
      };
      acquired = null;
      this.sendMediaState();
    } catch (error) {
      if (
        token === this.generation &&
        !(
          error instanceof Error &&
          ["NotAllowedError", "AbortError"].includes(error.name)
        )
      )
        this.context.notify("تعذر بدء مشاركة الشاشة. حاول مجددًا.");
    } finally {
      acquired?.getTracks().forEach((track) => track.stop());
      // The browser may stop capture while replaceTrack is still settling.
      if (
        this.settleCamera(wires, token) &&
        wires.screen?.getVideoTracks()[0]?.readyState === "ended"
      )
        void this.stopScreenShare();
    }
  };
}
